import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, Star, Truck, Check } from 'lucide-react';
import { useAddToCart } from '../../hooks/useAddToCart';

const ProductCard = ({ product, type, compact = false }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const { handleAddToCart } = useAddToCart();

  const calculatePrice = () => {
    let price = product.price || 0;
    if (selectedOption) {
      const key = Object.keys(selectedOption)[0];
      price += selectedOption[key];
    }
    return price;
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const productToAdd = {
      ...product,
      type,
      quantity: 1,
      customMessage: '',
      selectedOption,
      finalPrice: calculatePrice()
    };
    
    handleAddToCart(productToAdd, true);
  };

  const renderOptions = () => {
    if (!product.brifPrize || product.brifPrize.length === 0) return null;

    return (
      <div className="mt-2 relative">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowOptions(!showOptions);
          }}
          className="w-full text-left text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50 flex justify-between items-center"
        >
          {selectedOption ? (
            <span className="text-gray-700">
              {Object.keys(selectedOption)[0]}: +₹{selectedOption[Object.keys(selectedOption)[0]]}
            </span>
          ) : (
            <span className="text-gray-600">Select customization</span>
          )}
          <span className="text-xs">▼</span>
        </button>

        {showOptions && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg">
            {product.brifPrize.map((option, index) => {
              const key = Object.keys(option)[0];
              const value = option[key];
              return (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedOption(option);
                    setShowOptions(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-pink-50 text-sm ${
                    selectedOption === option ? 'bg-pink-50 text-pink-600' : 'text-gray-700'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>{key}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">+₹{value}</span>
                      {selectedOption === option && (
                        <Check className="h-3 w-3 text-green-500" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (compact) {
    return (
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 overflow-hidden">
        <Link onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} >
          <div className="relative h-32">
            <img 
              src={product.image} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {product.discount && (
              <span className="absolute top-1 left-1 bg-pink-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                {product.discount}% OFF
              </span>
            )}
            {type === 'combo' && (
              <span className="absolute top-1 right-1 bg-purple-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                COMBO
              </span>
            )}
          </div>
        </Link>

        <div className="p-2">
          <Link onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} >
            <h3 className="font-bold text-gray-900 text-xs mb-0.5 truncate">{product.name}</h3>
            <p className="text-xs text-gray-500 mb-1.5 line-clamp-1">{product.description}</p>
          </Link>

          {renderOptions()}

          <div className="flex items-center justify-between mt-1.5">
            <div>
              <span className="font-bold text-gray-900 text-sm">₹{calculatePrice()}</span>
              {product.originalPrice && (
                <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
              )}
            </div>
            <button 
            onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} 
              className="bg-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full hover:bg-pink-700 transition-colors"
            >
              View
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 overflow-hidden">
      <Link onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} >
        <div className="relative h-48">
          <img 
            src={product.image} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
          {product.discount && (
            <span className="absolute top-2 left-2 bg-pink-600 text-white text-xs font-bold px-2 py-1 rounded">
              {product.discount}% OFF
            </span>
          )}
          {type === 'combo' && (
            <span className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
              COMBO
            </span>
          )}
        </div>
      </Link>

      <div className="p-3">
        <Link onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} >
          <h3 className="font-bold text-gray-900 text-sm mb-1">{product.name}</h3>
          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{product.description}</p>
        </Link>

        {renderOptions()}

        <div className="flex items-center justify-between mt-3">
          <div>
            <span className="font-bold text-gray-900 text-base">₹{calculatePrice()}</span>
            {product.originalPrice && (
              <span className="text-sm text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
            )}
          </div>
          <button 
          onClick={ ()=>window.location.href=`/product/${type}/${product.id}`} 
            className="bg-pink-600 text-white text-sm font-bold px-3 py-1.5 rounded-full hover:bg-pink-700 transition-colors flex items-center"
          >
            <ShoppingCart className="h-3.5 w-3.5 mr-1" />
            View
          </button>
        </div>

        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <div className="flex items-center">
            <Star className="h-3 w-3 text-yellow-400 fill-yellow-400 mr-0.5" />
            <span>{product.rating || 4.5}</span>
          </div>
          <div className="flex items-center">
            <Truck className="h-3 w-3 mr-0.5" />
            <span>Free Ship</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;